import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Box, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

export const ChapterView = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const [markers, setMarkers] = useState([]);
  const [chapterTitle, setChapterTitle] = useState('');

  useEffect(() => { loadMarkers(); }, [chapterId]);

  const loadMarkers = async () => {
    try {
      const res = await api.get(`/student/markers/${chapterId}`);
      setMarkers(res.data);
      const chapRes = await api.get('/chapters');
      const chapter = chapRes.data.find(c => c.id === chapterId);
      if (chapter) setChapterTitle(chapter.title);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <Layout title={chapterTitle || 'Chapter'}>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center gap-3">
          <Button data-testid="back-btn" variant="ghost" onClick={() => navigate(-1)} className="rounded-full hover:bg-indigo-50">
            <ArrowLeft className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Back</span>
          </Button>
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-indigo-900">{chapterTitle}</h1>
            <p className="text-xs sm:text-sm text-slate-500">{markers.length} AR concepts</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {markers.map((marker, i) => (
            <motion.div key={marker.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card data-testid={`marker-card-${i}`}
                className="cursor-pointer hover:border-indigo-300 hover:shadow-xl hover:-translate-y-1 transition-all active:scale-95 border-slate-200 overflow-hidden group"
                onClick={() => navigate(`/student/viewer/${marker.id}`)}>
                <div className="aspect-video bg-slate-100 overflow-hidden relative">
                  <img
                    src={marker.image_url}
                    alt={marker.concept_title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <Badge className="bg-indigo-600 text-white border-0 text-xs"><Sparkles className="w-3 h-3 mr-1" /> View 3D</Badge>
                  </div>
                </div>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start gap-2">
                    <Box className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <h3 className="font-bold text-sm text-indigo-900 truncate">{marker.concept_title}</h3>
                      <p className="text-xs text-slate-500 line-clamp-2">{marker.concept_description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {markers.length === 0 && (
          <div className="text-center py-12">
            <Box className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No AR markers available for this chapter yet</p>
          </div>
        )}
      </div>
    </Layout>
  );
};
